// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + white inner surface with subtle blue border
// - Left orange accent stripe section
// - Inputs with soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Replaced alert() with themed Snackbar (clean UX)
// - Business logic unchanged

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { startRegistration } from "@simplewebauthn/browser";
import { API_URL } from "./config";

export default function GuardRegister() {
  // ✅ Theme constants (same palette)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  // ✅ shared sx
  const pageWrapSx = {
    minHeight: "100vh",
    p: 2,
    backgroundColor: BG,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  };

  const rootPaperSx = {
    width: "100%",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [msg, setMsg] = useState("");
  const [deviceLabel, setDeviceLabel] = useState("Main Gate");

  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const showToast = (m, sev = "success") => setToast({ open: true, msg: m, sev });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const code = useMemo(() => new URLSearchParams(window.location.search).get("code"), []);

  const register = async () => {
    try {
      if (!code) {
        setMsg("");
        showToast("Missing pairing code", "error");
        return;
      }

      setMsg("Fetching options...");
      const opts = await axios.post(`${API_URL}/api/guard/pairing/options`, { code });

      setMsg("Creating passkey...");
      const attestation = await startRegistration(opts.data);

      setMsg("Saving passkey...");
      const done = await axios.post(`${API_URL}/api/guard/pairing/verify`, {
        code,
        attestation,
        deviceLabel,
      });

      const finalMsg = done.data.message || "Paired!";
      setMsg(finalMsg);
      showToast(finalMsg, "success");
    } catch (e) {
      console.error(e);
      const errMsg =
        e.response?.data?.message ||
        e.response?.data?.reason ||
        e.message ||
        "Failed to pair";
      showToast(errMsg, "error");
      setMsg("");
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Guard Device Pairing
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Register a passkey on this device
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Surface with left orange stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1, textAlign: "left" }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Device Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <TextField
                fullWidth
                label="Device Label"
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                sx={inputSx}
                helperText="Example: Main Gate, Hostel Gate, Back Gate"
              />

              <Box sx={{ mt: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button variant="contained" sx={primaryBtnSx} onClick={register}>
                  Register Passkey
                </Button>

                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  onClick={() => {
                    setDeviceLabel("Main Gate");
                    setMsg("");
                    closeToast();
                  }}
                >
                  Reset
                </Button>
              </Box>

              {msg ? (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.18)",
                    backgroundColor: "rgba(25,118,210,0.04)",
                  }}
                >
                  {msg}
                </Alert>
              ) : null}

              {/* optional: show the code if you want */}
              {/* {code ? (
                <Typography sx={{ mt: 2, fontSize: "0.85rem", opacity: 0.7 }}>
                  Pairing code: {code}
                </Typography>
              ) : null} */}
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={closeToast}>
        <Alert severity={toast.sev} onClose={closeToast} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
